import {BrowserBackendSelector, getDefaultDFBackends} from '@hconnect/apiclient'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {useMemo} from 'react'

import {
  EndpointGroup,
  registerDomainOverrideInterceptor,
  setDomainOverrides
} from '../api/domainOverride'

/** Maps the selected backend `API_URL` to its dedicated HProduce domain. */
const HPRODUCE_DOMAIN_BY_API_URL: Readonly<Record<string, string>> = {
  'https://api-qa.hconnect.heidelbergcement.com': 'https://api-qa.hproduce.digital',
  'https://api-uat.hconnect.heidelbergcement.com': 'https://api-uat.hproduce.digital',
  'https://api.hconnect.heidelbergcement.com': 'https://api.hproduce.digital'
}

/**
 * Bridges the `useHproduceDomain` feature flag into the Suite domain-override store.
 *
 * When the flag is enabled and the selected backend has a mapped HProduce domain
 * (currently only QA), requests of the `janus` endpoint group are redirected to that
 * domain; otherwise no override is applied.
 *
 * Implemented purely against the apiclient public API (a request interceptor on
 * `ApiClient.axiosInstance`); the shared apiclient package is not modified.
 *
 * The interceptor is registered (once) and the override applied synchronously during
 * render, so both are in place before any (gated) data query lower in the tree fires
 * its request — guaranteeing requests never hit the wrong host.
 */
export const useHproduceDomainOverride = (): void => {
  const isEnabled = useCommonFeatureFlag(FeatureFlagKey.UseHproduceDomain)

  registerDomainOverrideInterceptor()

  const hproduceApiUrl = useMemo(() => {
    const apiUrl = new BrowserBackendSelector(getDefaultDFBackends()).getSelectedBackend().API_URL
    return HPRODUCE_DOMAIN_BY_API_URL[apiUrl]
  }, [])

  const overrides = useMemo<ReadonlyMap<EndpointGroup, string>>(() => {
    if (isEnabled && hproduceApiUrl) {
      return new Map<EndpointGroup, string>([['janus', hproduceApiUrl]])
    }
    return new Map<EndpointGroup, string>()
  }, [isEnabled, hproduceApiUrl])

  // Intentional render-time side effect (NOT a `useEffect`): the override must be in the
  // store before any gated query lower in the tree fires its request. React renders
  // top-down, so a synchronous write here lands before child components even render,
  // whereas effects run bottom-up (children before parents) and would let a janus query
  // fire against the wrong host first. `overrides` is purely derived from `isEnabled` +
  // `hproduceApiUrl`, so StrictMode double-invocation / discarded concurrent renders are
  // harmless — they always re-apply the same correct value.
  setDomainOverrides(overrides)
}
